import React from "react";
import { Row, Col, Card, Badge } from "react-bootstrap";
import { FaBoxes, FaChartBar, FaChartLine, FaDollarSign } from "react-icons/fa";
import ReportCard from "./ReportCard";
import { PieChartJS, prepareChartData } from "./ChartComponents";

const InventoryReport = ({ dashboardData, formatCurrency, getStatusBadge }) => {
  // Prepare pie chart data for stock status
  const stockStatusData = [
    {
      name: "In Stock",
      value:
        dashboardData.inventory.totalItems -
        dashboardData.inventory.lowStockItems -
        dashboardData.inventory.outOfStockItems,
    },
    { name: "Low Stock", value: dashboardData.inventory.lowStockItems },
    { name: "Out of Stock", value: dashboardData.inventory.outOfStockItems },
  ];

  const stockStatusChartData = prepareChartData(
    stockStatusData.map((item) => item.name),
    [
      {
        label: "Items Count",
        data: stockStatusData.map((item) => item.value),
      },
    ],
    ["#28a745", "#ffc107", "#dc3545", "#007bff"] // Green, Yellow, Red, Blue
  );

  return (
    <>
      <Row className="g-4 mb-4">
        <Col lg={3}>
          <ReportCard
            title="Total Items"
            value={dashboardData.inventory.totalItems.toLocaleString()}
            icon={<FaBoxes />}
            color="primary"
            subtitle="Items in inventory"
          />
        </Col>
        <Col lg={3}>
          <ReportCard
            title="Low Stock"
            value={dashboardData.inventory.lowStockItems.toLocaleString()}
            icon={<FaChartBar />}
            color="warning"
            subtitle="Items need restocking"
          />
        </Col>
        <Col lg={3}>
          <ReportCard
            title="Out of Stock"
            value={dashboardData.inventory.outOfStockItems.toLocaleString()}
            icon={<FaChartLine />}
            color="danger"
            subtitle="Items not available"
          />
        </Col>
        <Col lg={3}>
          <ReportCard
            title="Stock Value"
            value={formatCurrency(dashboardData.inventory.stockValue)}
            icon={<FaDollarSign />}
            color="success"
            subtitle="Total inventory value"
          />
        </Col>
      </Row>
      <Row className="g-4">
        <Col lg={8}>
          <Card className="shadow-sm border-0 mb-5">
            <Card.Header className="bg-transparent">
              <h5 className="mb-0">
                <FaBoxes className="me-2 text-white" />
                Top Inventory Items by Value
              </h5>
              <small className="text-muted">
                Showing top 10 items by total stock value
              </small>
            </Card.Header>
            <Card.Body>
              {dashboardData.inventory.topItems.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Item Name</th>
                        <th>Stock Value</th>
                        <th>Quantity</th>
                        <th>Rate per Unit</th>
                        <th>Units</th>
                        <th>Category</th>
                        <th>Tax Status</th>
                        <th>Stock Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.inventory.topItems.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <Badge
                              bg={index < 3 ? "warning" : "secondary"}
                              className="fs-6"
                            >
                              #{index + 1}
                            </Badge>
                          </td>
                          <td className="fw-medium">{item.name}</td>
                          <td className="fw-bold text-success">
                            {formatCurrency(item.value)}
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <span className="fw-medium">
                                {item.quantity.toLocaleString()}
                              </span>
                              <small className="text-muted ms-1">units</small>
                            </div>
                          </td>
                          <td>{formatCurrency(item.rate || 0)}</td>
                          <td>
                            <Badge bg="info">{item.units || "Units"}</Badge>
                          </td>
                          <td>
                            <small className="text-muted">
                              {item.parentGroup}
                            </small>
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1">
                              {item.gstApplicable ? (
                                <Badge bg="success" size="sm">
                                  GST
                                </Badge>
                              ) : (
                                <Badge bg="secondary" size="sm">
                                  No GST
                                </Badge>
                              )}
                              {item.isTaxable ? (
                                <Badge bg="info" size="sm">
                                  Taxable
                                </Badge>
                              ) : (
                                <Badge bg="warning" size="sm">
                                  Non-Tax
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td>{getStatusBadge(item.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <FaBoxes size={60} className="text-muted mb-3" />
                  <p className="text-muted">No inventory data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <PieChartJS
            data={stockStatusChartData}
            title="Stock Status"
            height={350}
            options={{
              plugins: {
                legend: {
                  position: "bottom",
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      const label = context.label || "";
                      const value = context.parsed;
                      const total = context.dataset.data.reduce(
                        (a, b) => a + b,
                        0
                      );
                      const percentage =
                        total > 0 ? ((value / total) * 100).toFixed(1) : "0";
                      return `${label}: ${value} items (${percentage}%)`;
                    },
                  },
                },
              },
            }}
          />
        </Col>
      </Row>
    </>
  );
};

export default InventoryReport;
